import React, { useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import PropertyCard from "@/components/PropertyCard";

const PROPERTY_TYPES = ["appartement", "tussenwoning", "hoekwoning", "2-onder-1-kap", "vrijstaand", "bungalow"];
const FEATURES = ["tuin", "balkon", "garage", "parkeerplaats", "berging", "zonnepanelen", "vloerverwarming"];

export default function BuyerProfile() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", locations: "", budget_min: "", budget_max: "",
    property_types: [], min_bedrooms: "", min_area_m2: "", features: [], timeline: "1-3 maanden", notes: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (key, v) => setF((s) => ({ ...s, [key]: s[key].includes(v) ? s[key].filter((x) => x !== v) : [...s[key], v] }));

  const submit = async () => {
    if (!f.name || !f.email) return toast.error("Naam en email zijn verplicht");
    setBusy(true);
    try {
      const payload = {
        ...f,
        locations: f.locations ? f.locations.split(",").map(s => s.trim()).filter(Boolean) : [],
        budget_min: f.budget_min ? parseFloat(f.budget_min) : null,
        budget_max: f.budget_max ? parseFloat(f.budget_max) : null,
        min_bedrooms: f.min_bedrooms ? parseInt(f.min_bedrooms) : null,
        min_area_m2: f.min_area_m2 ? parseFloat(f.min_area_m2) : null,
      };
      const { data } = await http.post("/buyer/profile", payload);
      setResult(data);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-6xl mx-auto py-14 px-4">
      <div className="text-center max-w-2xl mx-auto">
        <CheckCircle2 className="w-16 h-16 text-[color:var(--wv-green)] mx-auto" />
        <h1 className="font-display text-4xl font-extrabold mt-4">Bedankt!</h1>
        <p className="mt-3 text-slate-600">Uw zoekprofiel is aangemaakt. Hier zijn direct de best passende woningen op basis van uw wensen.</p>
      </div>
      {result?.matches?.length > 0 ? (
        <div className="mt-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Direct gematcht</p>
          <h2 className="font-display text-2xl font-bold mt-1">Uw top {result.matches.length} matches</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="buyer-matches-grid">
            {result.matches.map((m) => (
              <div key={m.property.id} className="relative">
                <PropertyCard p={m.property} matchScore={m.score} />
                {m.matched?.length > 0 && (
                  <p className="mt-2 text-xs text-slate-600 line-clamp-2">✓ {m.matched.slice(0, 3).join(" · ")}</p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/woningen" className="wv-btn-outline" data-testid="buyer-see-all">Bekijk alle woningen</Link>
          </div>
        </div>
      ) : (
        <p className="text-center mt-6 text-slate-500">Nog geen directe matches. U ontvangt bericht zodra wij passende woningen publiceren.</p>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Zoekprofiel</p>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">Maak gratis een zoekprofiel</h1>
      <p className="mt-3 text-slate-600">"Vertel ons wat u zoekt. Wij zoeken verder."</p>

      <div className="mt-8 p-6 md:p-8 bg-white border border-slate-200 rounded-2xl grid md:grid-cols-2 gap-4">
        <div><label className="text-sm font-semibold">Naam</label><input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="buyer-name" /></div>
        <div><label className="text-sm font-semibold">Email</label><input className="wv-input mt-1" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="buyer-email" /></div>
        <div><label className="text-sm font-semibold">Telefoon (optioneel)</label><input className="wv-input mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="buyer-phone" /></div>
        <div><label className="text-sm font-semibold">Kooptermijn</label><select className="wv-input mt-1" value={f.timeline} onChange={(e) => setF({ ...f, timeline: e.target.value })} data-testid="buyer-timeline">{["direct", "<1 maand", "1-3 maanden", "3-6 maanden", "6-12 maanden", "oriënterend"].map(x => <option key={x}>{x}</option>)}</select></div>
        <div className="md:col-span-2">
          <label className="text-sm font-semibold">Zoekopdracht alerts — hoe vaak wilt u bericht?</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {[["direct","Direct bij nieuwe match"],["daily","Dagelijks overzicht"],["weekly","Wekelijks overzicht"],["off","Geen meldingen"]].map(([v,label])=>(
              <button type="button" key={v} onClick={() => setF({ ...f, alert_frequency: v })} className={`wv-chip ${f.alert_frequency === v ? "active" : ""}`} data-testid={`alert-${v}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2"><label className="text-sm font-semibold">Gewenste locaties (komma-gescheiden)</label><input className="wv-input mt-1" placeholder="Amsterdam, Utrecht, Almere" value={f.locations} onChange={(e) => setF({ ...f, locations: e.target.value })} data-testid="buyer-locations" /></div>
        <div><label className="text-sm font-semibold">Budget minimum (€)</label><input className="wv-input mt-1" type="number" value={f.budget_min} onChange={(e) => setF({ ...f, budget_min: e.target.value })} data-testid="buyer-budget-min" /></div>
        <div><label className="text-sm font-semibold">Budget maximum (€)</label><input className="wv-input mt-1" type="number" value={f.budget_max} onChange={(e) => setF({ ...f, budget_max: e.target.value })} data-testid="buyer-budget-max" /></div>
        <div><label className="text-sm font-semibold">Min. slaapkamers</label><input className="wv-input mt-1" type="number" value={f.min_bedrooms} onChange={(e) => setF({ ...f, min_bedrooms: e.target.value })} data-testid="buyer-min-bedrooms" /></div>
        <div><label className="text-sm font-semibold">Min. oppervlak (m²)</label><input className="wv-input mt-1" type="number" value={f.min_area_m2} onChange={(e) => setF({ ...f, min_area_m2: e.target.value })} data-testid="buyer-min-area" /></div>
        <div className="md:col-span-2">
          <label className="text-sm font-semibold">Gewenste woningtypes</label>
          <div className="mt-2 flex flex-wrap gap-2">{PROPERTY_TYPES.map((x) => <button key={x} type="button" onClick={() => toggle("property_types", x)} className={`wv-chip ${f.property_types.includes(x) ? "active" : ""}`} data-testid={`buyer-type-${x}`}>{x}</button>)}</div>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-semibold">Gewenste kenmerken</label>
          <div className="mt-2 flex flex-wrap gap-2">{FEATURES.map((x) => <button key={x} type="button" onClick={() => toggle("features", x)} className={`wv-chip ${f.features.includes(x) ? "active" : ""}`} data-testid={`buyer-feature-${x}`}>{x}</button>)}</div>
        </div>
        <div className="md:col-span-2"><label className="text-sm font-semibold">Extra opmerkingen</label><textarea className="wv-input mt-1" rows="3" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="buyer-notes" /></div>
        <div className="md:col-span-2 flex justify-end">
          <button onClick={submit} disabled={busy} className="wv-btn-green" data-testid="buyer-submit-btn">{busy ? "Verzenden…" : "Zoekprofiel aanmaken"}</button>
        </div>
      </div>
    </div>
  );
}
